/**
 * One "Only when" row, drawn like a condition row: what it guards (in the
 * metric box's place), ≥ and the number, its unit, the ⓘ and the ✕.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { type LucideIcon, X } from 'lucide-react';

import { HintDot, NumberField } from '@/components/ui';

export interface OnlyWhenRowProps {
  icon: LucideIcon;
  label: string;
  /** "≥" before the number; absent for a plain amount ("46 min"). */
  atLeast?: boolean;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max?: number;
  unit: ReactNode;
  /** After the unit — a choice that belongs to this row (in total / in a row). */
  extra?: ReactNode;
  hint: string;
  /** Absent = the row cannot be removed here (a shared setting). */
  onRemove?: () => void;
  testId: string;
}

export const OnlyWhenRow: FC<OnlyWhenRowProps> = ({ icon: Icon, label, atLeast, value, onChange, min, max, unit, extra, hint, onRemove, testId }) => (
  <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-md border border-border bg-card/60 px-2 py-1.5" data-testid={testId}>
    <span className="inline-flex h-8 w-44 max-w-[14rem] shrink-0 items-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-2 text-xs font-medium">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      {label}
    </span>
    {atLeast ? <span className="font-mono text-xs font-semibold text-muted-foreground">≥</span> : null}
    <NumberField value={value} onChange={onChange} min={min} max={max} aria-label={label} className="h-8 w-20 rounded-md border border-input bg-background px-2 text-right font-mono text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-ring" />
    <span className="text-xs text-muted-foreground">{unit}</span>
    {extra}
    <HintDot label={hint} />
    {onRemove ? (
      <button type="button" onClick={onRemove} aria-label={`Remove “${label}”`} className="ml-auto flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive">
        <X className="h-3.5 w-3.5" />
      </button>
    ) : null}
  </div>
);
